import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, TextInput } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, textStyles } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface ReminderPickerProps {
  /** Minutes-before-event values, matching CalendarEvent.reminders — the backend
   * Task pipeline (createReminderTasks) and local device scheduling both already
   * consume this shape, this is purely the missing UI to produce it. */
  value: number[];
  onChange: (reminders: number[]) => void;
}

// Mirrors Google Calendar's own common reminder offsets.
const PRESET_OPTIONS: { minutes: number; labelKey: 'calendar.eventForm.reminderAtTimeOfEvent' | 'calendar.eventForm.reminderMinutes5' | 'calendar.eventForm.reminderMinutes15' | 'calendar.eventForm.reminderMinutes30' | 'calendar.eventForm.reminderHour1' | 'calendar.eventForm.reminderDay1' }[] = [
  { minutes: 0, labelKey: 'calendar.eventForm.reminderAtTimeOfEvent' },
  { minutes: 5, labelKey: 'calendar.eventForm.reminderMinutes5' },
  { minutes: 15, labelKey: 'calendar.eventForm.reminderMinutes15' },
  { minutes: 30, labelKey: 'calendar.eventForm.reminderMinutes30' },
  { minutes: 60, labelKey: 'calendar.eventForm.reminderHour1' },
  { minutes: 1440, labelKey: 'calendar.eventForm.reminderDay1' },
];

export const ReminderPicker: React.FC<ReminderPickerProps> = ({ value, onChange }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [customInput, setCustomInput] = useState('');

  const toggle = (minutes: number) => {
    if (value.includes(minutes)) {
      onChange(value.filter((m) => m !== minutes));
    } else {
      onChange([...value, minutes].sort((a, b) => a - b));
    }
  };

  const addCustom = () => {
    const minutes = parseInt(customInput, 10);
    if (!isNaN(minutes) && minutes >= 0 && !value.includes(minutes)) {
      onChange([...value, minutes].sort((a, b) => a - b));
    }
    setCustomInput('');
  };

  const customValues = value.filter((m) => !PRESET_OPTIONS.some((p) => p.minutes === m));

  const formatCustom = (minutes: number): string => {
    if (minutes === 0) return t('calendar.eventForm.reminderAtTimeOfEvent');
    if (minutes % 1440 === 0) return t('calendar.eventForm.reminderDaysBefore', { count: minutes / 1440 });
    if (minutes % 60 === 0) return t('calendar.eventForm.reminderHoursBefore', { count: minutes / 60 });
    return t('calendar.eventForm.reminderMinutesBefore', { count: minutes });
  };

  return (
    <View style={styles.field}>
      <Text style={styles.label}>{t('calendar.eventForm.remindersLabel')}</Text>
      <Text style={styles.helperText}>{t('calendar.eventForm.remindersHelperText')}</Text>

      <View style={[styles.chipGrid, { flexDirection: rtl.rowDirection }]}>
        {PRESET_OPTIONS.map((option) => {
          const isSelected = value.includes(option.minutes);
          return (
            <TouchableOpacity
              key={option.minutes}
              style={[styles.chip, isSelected && styles.chipSelected]}
              onPress={() => toggle(option.minutes)}
            >
              <Text style={[styles.chipText, isSelected && styles.chipTextSelected]}>
                {t(option.labelKey)}
              </Text>
            </TouchableOpacity>
          );
        })}
        {customValues.map((minutes) => (
          <TouchableOpacity key={minutes} style={[styles.chip, styles.chipSelected]} onPress={() => toggle(minutes)}>
            <Text style={[styles.chipText, styles.chipTextSelected]}>{formatCustom(minutes)}</Text>
            <Ionicons name="close" size={14} color={colors.primary as string} />
          </TouchableOpacity>
        ))}
      </View>

      <View style={[styles.customRow, { flexDirection: rtl.rowDirection }]}>
        <TextInput
          style={styles.customInput}
          value={customInput}
          onChangeText={setCustomInput}
          placeholder={t('calendar.eventForm.reminderCustomPlaceholder')}
          placeholderTextColor={colors.textLight as string}
          keyboardType="numeric"
          onSubmitEditing={addCustom}
        />
        <TouchableOpacity style={styles.addButton} onPress={addCustom} disabled={!customInput}>
          <Ionicons name="add" size={18} color={colors.primary as string} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  field: {
    marginBottom: spacing.md,
  },
  label: {
    ...textStyles.body,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.xs,
  },
  helperText: {
    ...textStyles.caption,
    fontWeight: textStyles.caption.fontWeight as any,
    color: colors.textLight,
    marginBottom: spacing.sm,
  },
  chipGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background.primary,
  },
  chipSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.brand.primaryLight,
  },
  chipText: {
    ...textStyles.caption,
    fontWeight: textStyles.caption.fontWeight as any,
    color: colors.textDark,
  },
  chipTextSelected: {
    color: colors.primary as string,
    fontWeight: '600',
  },
  customRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  customInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    minHeight: 44,
    color: colors.textDark as string,
  },
  addButton: {
    width: 44,
    height: 44,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
